import _ from 'lodash';

export default {
	data() {
		return {
			undoRedoHistory: [],
			isUndoRedoExecuting: false,
			undoHistoryLimits: {
				audio: 20
			}
		};
	},
	computed: {
		lastUndoOperation() {
			let hist = this.undoHistory();
			if (!hist.length) return;
			return `${this.$tCaption('Undo')} ${hist.slice(-1)[0]?.text}`;
		},
		nextRedoOperation() {
			let hist = this.redoHistory();
			if (!hist.length) return;
			return `${this.$tCaption('Redo')} ${hist[0]?.text}`;
		}
	},
	methods: {
		defineBaseUndoRedoMethods(initiator, additionalCallback) {
			if (!initiator.hasOwnProperty('__getUndoHistory')) {
				const applyVersionData = (data) => this.applyVersionData(initiator, data);
				Object.defineProperty(initiator, '__applyVersionData', {
					get: () => applyVersionData
				});

				const updateLastVersion = () => {
					if (__DEBUG__)
						console.debug('UNDOREDO: Update last version');
					initiator.__lastVersion = initiator.__getVersionData();
				}
				Object.defineProperty(initiator, '__updateLastVersion', {
					get: () => updateLastVersion
				});
				
				const getUndoHistory = () => this.undoHistory().filter(u => u.initiator === initiator);
				Object.defineProperty(initiator, '__getUndoHistory', {
					get: () => getUndoHistory
				});

				Object.defineProperty(initiator, '__hasUndoHistory', {
					get: () => !!getUndoHistory().length
				});

				const clearRedo = () => {
					if (__DEBUG__)
						console.debug('Clear redo');
					if (this.redoHistory().length)
						_.remove(this.undoRedoHistory, f => f.undoExecuted)
				};

				const checkUndoRedoLength = (group) => {
					let groupLimit = this.undoHistoryLimits[group];
					if (groupLimit > 0) {
						let groupItems = this.undoRedoHistory.filter(f => f.group === group);
						if (groupItems.length > groupLimit) {
							this.undoRedoHistory.splice(this.undoRedoHistory.findIndex(i => i === groupItems[0]), 1);
						}
					}
				}

				const getComparableData = (data) => {
					return {
						...data
					}
				}

				const hasChanges = () => {
					const lastData = initiator.__lastVersion || initiator.__getVersionData();
					const nextData = initiator.__getVersionData();
					const isSame = JSON.stringify(getComparableData(lastData)) === JSON.stringify(getComparableData(nextData));
					return isSame ? false : {
						old: getComparableData(lastData),
						new: getComparableData(nextData)
					};
				}

				Object.defineProperty(initiator, '__hasChanges', {
					get: () => hasChanges
				});

				var getChanges = (oldValue, newValue) => {
					if (!oldValue || !newValue) return '';

					let changedOptions = [];

					let checkStruct = (o, n, key) => {
						if (`${key}`.startsWith('_')) return;
						if (o != null && typeof(o) === 'object' && !Array.isArray(o) && !Array.isArray(n)) {
							Object.keys(o).map(k => {
								if (n?.hasOwnProperty(k)) {
									checkStruct(o[k], n[k], k);
								} else {
									if (`${k}`.startsWith('_')) return;
									changedOptions.push({
										key: k,
										oldValue: o[k],
										newValue: null,
										status: 2
									});
								}
							});

							Object.keys(n).map(k => {
								if (!o?.hasOwnProperty(k)) {
									if (`${k}`.startsWith('_')) return;
									changedOptions.push({
										key: k,
										oldValue: null,
										newValue: n[k],
										status: 1
									});
								}
							});
						} else {
							let isSame = true;
							if (Array.isArray(o)) {
								isSame = JSON.stringify(o) === JSON.stringify(n);
							} else {
								isSame = `${o}` == `${n}`;
							}
		
							if (!isSame) {
								changedOptions.push({
									key,
									oldValue: o,
									newValue: n,
									status: 0
								});
							}
						}
					}

					checkStruct(oldValue, newValue);
					return changedOptions;
				}

				let getChangesText = (oldValue, newValue) => {
					let changes = getChanges(oldValue, newValue);

					return changes.map(f => {
						let objValue = f.newValue != null && typeof(f.newValue) !== 'object' ? ` (${f.newValue})` : '';
						return `${newValue.caption}, ${this.$Data.spaceByUpperCase(`${f.key[0].toUpperCase()}${f.key.substr(1)}`)} ${[this.$tCaption('changed'), this.$tCaption('added'), this.$tCaption('removed')][f.status]}${objValue}`
					}).join(', ');
				}

				const addDataToUndoHistory = (opts) => {
					if (this.isPublishing) return;
					const lastData = opts?.lastData || initiator.__lastVersion || initiator.__getVersionData();
					const nextData = opts?.nextData || initiator.__getVersionData();
					const isSame = JSON.stringify(getComparableData(lastData)) === JSON.stringify(getComparableData(nextData));
					
					if (__DEBUG__)
						console.debug('addDataToUndoHistory', { lastData, nextData, isSame });
					
					if (isSame) return;

					let changesText = getChangesText(lastData, nextData);

					if (!changesText) return;
					
					this.isUndoRedoExecuting = true;
					this.undoRedoHistory.push({ 
						lastData, 
						nextData,
						initiator,
						text: opts?.text || changesText,
						undoExecuted: false,
						date: new Date()
					});
					initiator.__isRestoredFromUndo = false;
					clearRedo();
					updateLastVersion();

					setImmediate(() => {
						if (this._isDestroyed) return;
						this.isUndoRedoExecuting = false;
					})
				};

				const addDataToUndoHistoryDebounced = _.debounce(addDataToUndoHistory, 300, {
					'leading': false,
					'trailing': true
				})

				const addDataToHistoryChecked = (opts) => {
					if (this.isUndoRedoExecuting || this.isPublishMode) return;
					addDataToUndoHistoryDebounced(opts);
				}

				Object.defineProperty(initiator, '__addDataToUndoHistory', {
					get: () => addDataToHistoryChecked
				});

				const undoTrackHistory = () => {
					if (initiator.__hasUndoHistory) {
						this.undo(_.last(initiator.__getUndoHistory()));
					}
				}
				Object.defineProperty(initiator, '__undo', {
					get: () => undoTrackHistory
				});

				if (additionalCallback)
					additionalCallback({ checkUndoRedoLength, clearRedo, getComparableData });
			}
		},
		defineTrackUndoRedoMethods(initiator) {
			this.defineBaseUndoRedoMethods(initiator, (undoOpts) => {
				const getVersionData = () => this.getVersionData(initiator, 'track');
				Object.defineProperty(initiator, '__getVersionData', {
					get: () => getVersionData
				});

				const addTrackAudioToUndoHistory = (opts) => {
					if (this.isUndoRedoExecuting || opts?.lastBase64 === opts?.nextBase64 || this.isPublishMode) return;
					const lastData = _.cloneDeep(opts?.lastData || initiator.__lastVersion || initiator.__getVersionData());
					const nextData = _.cloneDeep(opts?.nextData || initiator.__getVersionData());
					const isSame = JSON.stringify(undoOpts.getComparableData(lastData)) === JSON.stringify(undoOpts.getComparableData(nextData)) && opts?.lastBase64 === opts?.nextBase64;
					
					if (isSame) return;

					if (__DEBUG__)
						console.debug('UNDOREDO: addTrackAudioToUndoHistory', { l: _.cloneDeep(lastData), n: _.cloneDeep(nextData) });

					this.undoRedoHistory.push({
						...(opts || {}),
						lastBase64: opts?.lastBase64,
						nextBase64: opts?.nextBase64,
						lastData,
						nextData,
						initiator,
						text: opts?.text || `${this.$tCaption('Clip')} "${this.$Data.limitText(initiator?.name(), 10)}" ${this.$tCaption('Audio change')}`,
						undoExecuted: false,
						type: 'track',
						group: 'audio',
						date: new Date()
					});
					initiator.__isRestoredFromUndo = false;
					undoOpts?.checkUndoRedoLength('audio');
					undoOpts?.clearRedo();
				};

				Object.defineProperty(initiator, '__addTrackAudioToUndoHistory', {
					get: () => addTrackAudioToUndoHistory
				});
			});
		},
		defineAggregatedTrackUndoRedoMethods(initiator) {
			this.defineBaseUndoRedoMethods(initiator, () => {
				const getVersionData = () => this.getVersionData(initiator, 'aggregatedTrack');
				Object.defineProperty(initiator, '__getVersionData', {
					get: () => getVersionData
				});

				initiator.__updateLastVersion();
			});
		},
		defineCreativeUndoRedoMethods(initiator) {
			this.defineBaseUndoRedoMethods(initiator, () => {
				const getVersionData = () => this.getVersionData(initiator, 'creative');
				Object.defineProperty(initiator, '__getVersionData', {
					get: () => getVersionData
				});
				initiator.__updateLastVersion();
			});
		},
		undoHistory() {
			return this.undoRedoHistory.filter(f => !f.undoExecuted);
		},
		redoHistory() {
			return this.undoRedoHistory.filter(f => f.undoExecuted);
		},
		async undo(undoItem) {
			if (this.isUndoRedoExecuting || this.isPublishMode) return false;
			this.isUndoRedoExecuting = true;
			this.isLoading++;
			try {
				undoItem = undoItem || _.last(this.undoHistory());
				var initiator = undoItem?.initiator;
				if (initiator) {
					if (initiator.__isLoading !== void 0)  {
						initiator.__isLoading++;
					}
					try {
						if (undoItem?.type === 'track' && undoItem?.lastBase64) {
							initiator?.__tracker?.setBase64ForTrack(initiator, undoItem?.lastBase64);
							await this.$azure.uploadInsertionAudio(initiator.insertion, undoItem?.lastBase64);
						}

						if (undoItem?.lastData) {
							initiator.__applyVersionData(undoItem?.lastData);
						}
						undoItem.undoExecuted = true;
						initiator.__isRestoredFromUndo = true;
					} finally {
						if (initiator.__isLoading !== void 0)  {
							initiator.__isLoading--;
						}	
					}
				} else {
					this.undoRedoHistory.splice(_.findIndex(this.undoRedoHistory, undoItem), 1);
				}
			} finally {
				this.$nextTick(() => {
					this.$nextTick(() => {
						this.isUndoRedoExecuting = false;
					});
				});
				this.isLoading--;
			}
			return true;
		},
		async redo(redoItem) {
			if (this.isUndoRedoExecuting || this.isPublishMode) return false;
			this.isUndoRedoExecuting = true;
			this.isLoading++;
			try {
				redoItem = redoItem || this.redoHistory()[0];
				var initiator = redoItem?.initiator;
				if (initiator) {
					if (initiator.__isLoading !== void 0)  {
						initiator.__isLoading++;
					}
					try {
						if (redoItem?.type === 'track' && redoItem?.nextBase64) {
							initiator?.__tracker?.setBase64ForTrack(initiator, redoItem?.nextBase64);
							initiator.duration = redoItem?.duration || initiator.duration;
							await this.$azure.uploadInsertionAudio(initiator.insertion, redoItem?.nextBase64);
						}
						if (redoItem?.nextData) {
							initiator.__applyVersionData(redoItem?.nextData);
						}
						redoItem.undoExecuted = false;
						initiator.__isRestoredFromUndo = true;
					} finally {
						if (initiator.__isLoading !== void 0)  {
							initiator.__isLoading--;
						}
					}
				} else {
					this.undoRedoHistory.splice(_.findIndex(this.undoRedoHistory, redoItem), 1);
				}
			} finally {
				this.$nextTick(() => {
					this.$nextTick(() => {
						this.isUndoRedoExecuting = false;
					});
				});
				this.isLoading--;
			}
			return true;
		},
		getVersionData(initiator, type) {
			var version = {};
			switch(type) {
				case 'track': {
					const track = initiator;
					version.trackData = {
						start: parseFloat(track?.start?.toFixed(3)),
						startOffset: parseFloat(track?.startOffset?.toFixed(3)),
						endOffset: parseFloat(track?.endOffset?.toFixed(3))
					};
					version.trackNumber = track.trackNumber();
					version.insertionConfig = _.cloneDeep(track?.insertion?.JsonConfig);
					version.caption = `${this.$tCaption('Clip')} "${this.$Data.limitText(track.name(), 10)}"`;

					if (track.type === 'textToSpeech') {
						version.ssmlData = {
							voice: track.insertion.Voice,
							language: track.insertion.Language,
							ssml: track.insertion.Ssml,
							rate: track.insertion.Rate,
							pitch: track.insertion.Pitch
						}
					}
					break;
				}	
				case 'aggregatedTrack': {
					const aggregatedTrack = initiator;
					version.data = _.cloneDeep(aggregatedTrack);
					version.caption = initiator.name;
					delete version.data.__lastVersion;
					break;
				}
				case 'creative': {
					const creative = initiator;
					version.jsonConfig = _.omit(creative.JsonConfig, ['aggregatedTracks', 'creativeVersion', 'editingLock']);
					version.caption = this.$tCaption('Creative');
					break;
				}
			}

			return {
				type,
				...version
			};
		},
		applyVersionData(initiator, version) {
			if (!version || !initiator) return;
			switch (version?.type) {
				case 'track': {
					_.assign(initiator, version.trackData);
					if (version.trackNumber !== void 0 && version.trackNumber !== initiator.trackNumber()) {
						initiator.insertion.JsonConfig.trackNumber = version.trackNumber;
					}
					if (initiator.type === 'textToSpeech' && version.ssmlData) {
						initiator.insertion.Voice = version.ssmlData.voice;
						initiator.insertion.Language = version.ssmlData.language;
						initiator.insertion.Ssml = version.ssmlData.ssml;
						initiator.insertion.Rate = version.ssmlData.rate;
						initiator.insertion.Pitch = version.ssmlData.pitch;
					}
					if (__DEBUG__)
						console.debug('UNDOREDO: apply ssml', { dataa: version.ssmlData, it: initiator.type })
					break;
				}
				case 'aggregatedTrack': {
					_.assign(initiator, version.data);
					break;
				}
				case 'creative': {
					_.assign(initiator.JsonConfig, version.jsonConfig);
					break;
				}
			}
			initiator.__updateLastVersion();
		},
	}
};
